import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  FaJava,
  FaPython,
  FaPhp,
  FaJs,
  FaHtml5,
  FaCss3Alt,
  FaReact,
  FaBootstrap,
  FaLaravel,
  FaNodeJs,
  FaAndroid,
  FaDocker,
  FaGithub,
  FaGitlab,
  FaGoogle,
  FaJenkins,
  FaFigma,
  FaCode,
  FaMicrosoft,
} from "react-icons/fa";
import {
  SiFlutter,
  SiDart,
  SiMongodb,
  SiPostgresql,
  SiMysql,
  SiFirebase,
  SiSpring,
  SiExpress,
  SiIonic,
  SiMaterialdesign,
  SiCanva,
  SiMulesoft,
  SiSalesforce,
} from "react-icons/si";
import { VscAzureDevops } from "react-icons/vsc";
import {
  TbBrandAdobePhotoshop,
  TbBrandAdobePremier,
  TbBrandAdobeAfterEffect,
} from "react-icons/tb";
import { DiIllustrator } from "react-icons/di";
import imageahmada from "../assets/images/png/ahmada.jpg";

const iconMap = {
  Java: FaJava,
  Python: FaPython,
  PHP: FaPhp,
  JavaScript: FaJs,
  HTML5: FaHtml5,
  CSS3: FaCss3Alt,
  Dart: SiDart,
  React: FaReact,
  "Material-UI": SiMaterialdesign,
  Bootstrap: FaBootstrap,
  Laravel: FaLaravel,
  "Express.js": SiExpress,
  Spring: SiSpring,
  "Node.js": FaNodeJs,
  Flutter: SiFlutter,
  Android: FaAndroid,
  Ionic: SiIonic,
  "React Native": FaReact,
  MySQL: SiMysql,
  PostgreSQL: SiPostgresql,
  MongoDB: SiMongodb,
  Firebase: SiFirebase,
  Salesforce: SiSalesforce,
  Mulesoft: SiMulesoft,
  "Google Cloud": FaGoogle,
  "Azure DevOps": VscAzureDevops,
  Jenkins: FaJenkins,
  Docker: FaDocker,
  GitHub: FaGithub,
  GitLab: FaGitlab,
  Azure: FaMicrosoft,
  Photoshop: TbBrandAdobePhotoshop,
  Illustrator: DiIllustrator,
  "Premiere Pro": TbBrandAdobePremier,
  "After Effects": TbBrandAdobeAfterEffect,
  Figma: FaFigma,
  Canva: SiCanva,
};

const categories = [
  {
    key: "catLanguages",
    items: ["Java", "Python", "PHP", "JavaScript", "HTML5", "CSS3", "Dart"],
  },
  { key: "catFrontend", items: ["React", "Material-UI", "Bootstrap"] },
  { key: "catBackend", items: ["Laravel", "Express.js", "Spring", "Node.js"] },
  { key: "catMobile", items: ["Flutter", "Android", "Ionic", "React Native"] },
  { key: "catDatabase", items: ["MySQL", "PostgreSQL", "MongoDB", "Firebase"] },
  { key: "catCloud", items: ["Salesforce", "Mulesoft", "Google Cloud"] },
  { key: "catDevops", items: ["Azure DevOps", "Jenkins", "Docker"] },
  { key: "catVersioning", items: ["GitHub", "GitLab", "Azure"] },
  {
    key: "catDesign",
    items: [
      "Photoshop",
      "Illustrator",
      "Premiere Pro",
      "After Effects",
      "Figma",
      "Canva",
    ],
  },
];

const allSkills = categories.flatMap((c) => c.items);

const categoryColors = {
  catLanguages: "#DEB887",
  catFrontend: "#F4C778",
  catBackend: "#C4956A",
  catMobile: "#E8C99B",
  catDatabase: "#D4A06A",
  catCloud: "#FFD699",
  catDevops: "#B8956A",
  catVersioning: "#CDAA7D",
  catDesign: "#F0D9B5",
};

function getCategoryColor(skillName) {
  for (const cat of categories) {
    if (cat.items.includes(skillName)) return categoryColors[cat.key];
  }
  return "#DEB887";
}

/* ---- 3D math ---- */
function genGlobePts(r, latN, lonN) {
  const pts = [];
  for (let i = 1; i < latN; i++) {
    const lat = (Math.PI * i) / latN - Math.PI / 2;
    const cr = r * Math.cos(lat);
    const y = r * Math.sin(lat);
    const n = Math.max(8, Math.round(lonN * Math.cos(lat)));
    for (let j = 0; j < n; j++) {
      const lon = (2 * Math.PI * j) / n;
      pts.push({ x: cr * Math.cos(lon), y: -y, z: cr * Math.sin(lon) });
    }
  }
  return pts;
}

function genCircle(r, val, axis, n = 64) {
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const a = (2 * Math.PI * i) / n;
    if (axis === "lat") {
      const cr = r * Math.cos(val);
      pts.push({
        x: cr * Math.cos(a),
        y: -r * Math.sin(val),
        z: cr * Math.sin(a),
      });
    } else {
      const lat = (Math.PI * i) / n - Math.PI / 2;
      const cr = r * Math.cos(lat);
      pts.push({
        x: cr * Math.cos(val),
        y: -r * Math.sin(lat),
        z: cr * Math.sin(val),
      });
    }
  }
  return pts;
}

function genArc(r, sLa, sLo, eLa, eLo, h, n = 40) {
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const la = sLa + (eLa - sLa) * t;
    const lo = sLo + (eLo - sLo) * t;
    const cr = r + Math.sin(Math.PI * t) * h;
    pts.push({
      x: cr * Math.cos(la) * Math.cos(lo),
      y: -cr * Math.sin(la),
      z: cr * Math.cos(la) * Math.sin(lo),
    });
  }
  return pts;
}

const rY = (p, a) => ({
  x: p.x * Math.cos(a) + p.z * Math.sin(a),
  y: p.y,
  z: -p.x * Math.sin(a) + p.z * Math.cos(a),
});
const rX = (p, a) => ({
  x: p.x,
  y: p.y * Math.cos(a) - p.z * Math.sin(a),
  z: p.y * Math.sin(a) + p.z * Math.cos(a),
});
const pr = (p, cx, cy, f) => {
  const s = f / (f + p.z);
  return { x: cx + p.x * s, y: cy + p.y * s, z: p.z, scale: s };
};

function segFront(proj, z = 60) {
  const segs = [];
  let c = [];
  proj.forEach((p) => {
    if (p.z < z) c.push(p);
    else {
      if (c.length > 1) segs.push([...c]);
      c = [];
    }
  });
  if (c.length > 1) segs.push(c);
  return segs;
}

const toD = (pts) =>
  pts
    .map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(" ");

/* ========== COMPONENT ========== */
export default function SkillGlobe() {
  const [time, setTime] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const last = useRef({ x: 0, y: 0 });
  const cRef = useRef(null);
  const [sz, setSz] = useState(500);

  useEffect(() => {
    let f;
    const lp = () => {
      setTime((t) => t + 0.008);
      f = requestAnimationFrame(lp);
    };
    f = requestAnimationFrame(lp);
    return () => cancelAnimationFrame(f);
  }, []);

  useEffect(() => {
    const m = () => {
      if (cRef.current) {
        const r = cRef.current.getBoundingClientRect();
        setSz(Math.min(r.width, r.height, 600));
      }
    };
    m();
    window.addEventListener("resize", m);
    return () => window.removeEventListener("resize", m);
  }, []);

  const R = sz * 0.28;
  const CX = sz / 2;
  const CY = sz / 2;
  const FOV = sz * 1.1;
  const rotYA = time * 0.6 + drag.x;
  const rotXA = 0.25 + drag.y;

  const xf = useCallback(
    (pt) => pr(rY(rX(pt, rotXA), rotYA), CX, CY, FOV),
    [rotXA, rotYA, CX, CY, FOV],
  );
  const xfP = useCallback((pts) => pts.map(xf), [xf]);

  const onD = (e) => {
    e.preventDefault();
    setDragging(true);
    const p = e.touches ? e.touches[0] : e;
    last.current = { x: p.clientX, y: p.clientY };
  };
  const onM = (e) => {
    if (!dragging) return;
    const p = e.touches ? e.touches[0] : e;
    setDrag((d) => ({
      x: d.x + (p.clientX - last.current.x) * 0.005,
      y: d.y + (p.clientY - last.current.y) * 0.005,
    }));
    last.current = { x: p.clientX, y: p.clientY };
  };
  const onU = () => setDragging(false);

  const gDots = useMemo(() => genGlobePts(R, 14, 20), [R]);
  const latC = useMemo(() => {
    const c = [];
    for (let i = 1; i < 6; i++)
      c.push(genCircle(R, (Math.PI * i) / 6 - Math.PI / 2, "lat"));
    return c;
  }, [R]);
  const lonC = useMemo(() => {
    const c = [];
    for (let i = 0; i < 8; i++)
      c.push(genCircle(R, (Math.PI * 2 * i) / 8, "lon"));
    return c;
  }, [R]);

  const arcs = useMemo(
    () => [
      { sLa: 0.3, sLo: 0, eLa: -0.5, eLo: 2.1, h: R * 0.5 },
      { sLa: -0.2, sLo: 1, eLa: 0.7, eLo: 3.5, h: R * 0.6 },
      { sLa: 0.5, sLo: 2.5, eLa: -0.3, eLo: 5, h: R * 0.45 },
      { sLa: -0.4, sLo: 3.8, eLa: 0.6, eLo: 1.2, h: R * 0.55 },
      { sLa: 0.1, sLo: 4.5, eLa: -0.6, eLo: 0.5, h: R * 0.65 },
      { sLa: 0.7, sLo: 5.5, eLa: -0.1, eLo: 2.8, h: R * 0.5 },
    ],
    [R],
  );

  const skA = useMemo(() => {
    const g = Math.PI * (3 - Math.sqrt(5));
    return allSkills.map((s, i) => ({
      skill: s,
      theta: g * i,
      phi: Math.acos(1 - (2 * (i + 0.5)) / allSkills.length),
      idx: i,
    }));
  }, []);

  // --- dots ---
  const dotsEl = gDots.map((pt, i) => {
    const p = xf(pt);
    if (p.z > 0) return null;
    return (
      <circle
        key={i}
        cx={p.x}
        cy={p.y}
        r={1.1 * p.scale}
        fill="#DEB887"
        opacity={Math.max(0.08, Math.min(0.5, (FOV - p.z) / (FOV * 2)))}
      />
    );
  });

  // --- grid lines ---
  const gridEl = [...latC, ...lonC].flatMap((c, ci) =>
    segFront(xfP(c), 50).map((seg, si) => (
      <path
        key={`g${ci}-${si}`}
        d={toD(seg)}
        fill="none"
        stroke="#DEB887"
        strokeWidth="0.5"
        opacity="0.12"
      />
    )),
  );

  // --- arcs + traveling dots ---
  const arcEl = arcs.flatMap((a, ai) => {
    const pts = genArc(R, a.sLa, a.sLo, a.eLa, a.eLo, a.h);
    const pj = xfP(pts);
    const segs = segFront(pj, 80);
    const dt = (time * (0.3 + ai * 0.08)) % 1;
    const di = Math.min(Math.floor(dt * (pj.length - 1)), pj.length - 1);
    const dp = pj[di];
    const show = dp && dp.z < 80;
    return [
      ...segs.map((seg, si) => (
        <path
          key={`a${ai}-${si}`}
          d={toD(seg)}
          fill="none"
          stroke="#DEB887"
          strokeWidth="1.2"
          opacity="0.35"
          strokeDasharray="4 3"
        />
      )),
      show && (
        <g key={`ad${ai}`}>
          <circle
            cx={dp.x}
            cy={dp.y}
            r={3.5 * dp.scale}
            fill="#DEB887"
            opacity="0.9"
            style={{ filter: "url(#glowSmall)" }}
          />
          <circle
            cx={dp.x}
            cy={dp.y}
            r={7 * dp.scale}
            fill="none"
            stroke="#DEB887"
            strokeWidth="0.8"
            opacity="0.25"
          />
        </g>
      ),
    ];
  });

  // --- orbital rings ---
  const ringEl = [0, 1, 2].flatMap((i) => {
    const tilt = 0.3 + i * 0.25;
    const rr = R + 40 + i * 20;
    const pts = [];
    for (let j = 0; j <= 64; j++) {
      const a = (Math.PI * 2 * j) / 64;
      let pt = {
        x: rr * Math.cos(a),
        y: rr * 0.3 * Math.sin(a),
        z: rr * Math.sin(a) * Math.sin(tilt),
      };
      pt = rX(pt, rotXA + tilt * 0.5);
      pt = rY(pt, rotYA + i * 0.5);
      pts.push(pr(pt, CX, CY, FOV));
    }
    return segFront(pts, 100).map((seg, si) => (
      <path
        key={`r${i}-${si}`}
        d={toD(seg)}
        fill="none"
        stroke="#DEB887"
        strokeWidth="0.5"
        opacity={0.1 + Math.sin(time + i) * 0.04}
        strokeDasharray="6 4"
      />
    ));
  });

  // --- particles ---
  const partEl = Array.from({ length: 20 }, (_, i) => {
    const a = (time * 0.3 + i * 0.31) % (Math.PI * 2);
    const d = R + 20 + Math.sin(time * 0.5 + i) * 40;
    return (
      <circle
        key={`p${i}`}
        cx={CX + Math.cos(a) * d}
        cy={CY + Math.cos(time * 0.2 + i * 0.5) * d * 0.6}
        r={0.8 + Math.sin(time * 0.7 + i) * 0.4}
        fill="#DEB887"
        opacity={Math.max(0.04, 0.08 + Math.sin(time + i) * 0.06)}
      />
    );
  });

  // --- skill icons with LUMINOUS EFFECTS ---
  const OR = R + R * 0.55;
  const skillNodes = skA.map(({ skill, theta, phi, idx }) => {
    const spd = 0.15 + (idx % 5) * 0.03;
    const ct = theta + time * spd;
    const x = OR * Math.sin(phi) * Math.cos(ct);
    const y = OR * Math.cos(phi);
    const z = OR * Math.sin(phi) * Math.sin(ct);
    const p = xf({ x, y, z });
    const front = p.z < 60;
    const op = front ? Math.min(1, Math.max(0.25, (100 - p.z) / 150)) : 0.06;
    const Ic = iconMap[skill] || FaCode;
    const col = getCategoryColor(skill);
    const s = Math.max(12, Math.min(24, 20 * p.scale));
    const isH = hovered === skill;

    const sr = R * 0.92;
    const sp = xf({
      x: sr * Math.sin(phi) * Math.cos(ct),
      y: sr * Math.cos(phi),
      z: sr * Math.sin(phi) * Math.sin(ct),
    });

    // pulse phase unique per skill
    const pulse = Math.sin(time * 2.5 + idx * 0.8);
    const pulseOp = 0.08 + pulse * 0.06;

    // light particle traveling along the arm
    const armT = (time * 0.8 + idx * 0.3) % 1;
    const lpx = sp.x + (p.x - sp.x) * armT;
    const lpy = sp.y + (p.y - sp.y) * armT;

    return {
      z: p.z,
      el: (
        <g
          key={skill}
          style={{ cursor: front ? "pointer" : "default" }}
          onMouseEnter={() => front && setHovered(skill)}
          onMouseLeave={() => setHovered(null)}
        >
          {front && (
            <>
              {/* connecting arm */}
              <line
                x1={sp.x}
                y1={sp.y}
                x2={p.x}
                y2={p.y}
                stroke={col}
                strokeWidth={isH ? 1.5 : 0.5}
                opacity={isH ? op * 0.7 : op * 0.35}
                strokeDasharray={isH ? "none" : "2 2"}
                style={{
                  transition: "all 0.3s",
                  filter: isH ? `drop-shadow(0 0 3px ${col})` : "none",
                }}
              />

              {/* light particle traveling along arm */}
              <circle
                cx={lpx}
                cy={lpy}
                r={1.5 * p.scale}
                fill={col}
                opacity={op * 0.5}
                style={{ filter: "url(#glowSmall)" }}
              />

              {/* anchor dot on globe surface */}
              <circle
                cx={sp.x}
                cy={sp.y}
                r={(isH ? 3 : 2) * sp.scale}
                fill={col}
                opacity={op * 0.6}
                style={{ filter: `drop-shadow(0 0 ${isH ? 6 : 3}px ${col})` }}
              />
            </>
          )}

          {/* outer pulsing halo ring — always visible on front */}
          {front && (
            <circle
              cx={p.x}
              cy={p.y}
              r={(s / 2 + 6) * p.scale}
              fill="none"
              stroke={col}
              strokeWidth={0.6}
              opacity={pulseOp * op}
            />
          )}

          {/* secondary larger halo — very subtle breathing */}
          {front && (
            <circle
              cx={p.x}
              cy={p.y}
              r={(s / 2 + 12) * p.scale}
              fill="none"
              stroke={col}
              strokeWidth={0.3}
              opacity={pulseOp * op * 0.4}
            />
          )}

          {/* soft glow disc behind the icon */}
          {front && (
            <circle
              cx={p.x}
              cy={p.y}
              r={s * 0.7}
              fill={col}
              opacity={isH ? 0.12 : 0.04}
              style={{ filter: "url(#glowLarge)" }}
            />
          )}

          {/* hover: extra expanding ring */}
          {isH && front && (
            <>
              <circle
                cx={p.x}
                cy={p.y}
                r={(s / 2 + 4) * p.scale}
                fill="none"
                stroke={col}
                strokeWidth={1.2}
                opacity={0.4}
                style={{ filter: `drop-shadow(0 0 8px ${col})` }}
              >
                <animate
                  attributeName="r"
                  values={`${(s / 2 + 4) * p.scale};${(s / 2 + 18) * p.scale};${(s / 2 + 4) * p.scale}`}
                  dur="2s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.4;0.05;0.4"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </circle>
              {/* second ripple offset */}
              <circle
                cx={p.x}
                cy={p.y}
                r={(s / 2 + 4) * p.scale}
                fill="none"
                stroke={col}
                strokeWidth={0.6}
                opacity={0.25}
              >
                <animate
                  attributeName="r"
                  values={`${(s / 2 + 8) * p.scale};${(s / 2 + 24) * p.scale};${(s / 2 + 8) * p.scale}`}
                  dur="2s"
                  begin="0.5s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.25;0.0;0.25"
                  dur="2s"
                  begin="0.5s"
                  repeatCount="indefinite"
                />
              </circle>
            </>
          )}

          {/* icon node */}
          <foreignObject
            x={p.x - s / 2}
            y={p.y - s / 2}
            width={s}
            height={s}
            style={{
              opacity: op,
              overflow: "visible",
              pointerEvents: front ? "auto" : "none",
            }}
          >
            <div
              style={{
                width: s,
                height: s,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                background: isH ? "rgba(222,184,135,0.25)" : "rgba(0,0,0,0.5)",
                border: `1.5px solid ${isH ? col : "rgba(222,184,135,0.25)"}`,
                transition: "all 0.3s ease",
                transform: isH ? "scale(1.6)" : "scale(1)",
                boxShadow: isH
                  ? `0 0 16px ${col}88, 0 0 32px ${col}33, inset 0 0 8px ${col}22`
                  : `0 0 6px ${col}15`,
              }}
            >
              <Ic
                size={s * 0.55}
                color={isH ? "#fff" : col}
                style={{
                  transition: "color 0.3s",
                  filter: isH ? `drop-shadow(0 0 6px ${col})` : "none",
                }}
              />
            </div>
          </foreignObject>

          {/* skill label on hover */}
          {isH && front && (
            <foreignObject
              x={p.x - 60}
              y={p.y + s / 2 + 8}
              width={120}
              height={28}
            >
              <div
                style={{
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#DEB887",
                  textShadow: `0 0 10px ${col}, 0 0 20px ${col}66`,
                  fontFamily: "monospace",
                  letterSpacing: 1,
                  background: "rgba(0,0,0,0.55)",
                  borderRadius: 4,
                  padding: "2px 8px",
                  border: `1px solid ${col}33`,
                }}
              >
                {skill}
              </div>
            </foreignObject>
          )}
        </g>
      ),
    };
  });

  // z-sort back to front
  skillNodes.sort((a, b) => b.z - a.z);

  return (
    <div
      ref={cRef}
      onMouseDown={onD}
      onMouseMove={onM}
      onMouseUp={onU}
      onMouseLeave={onU}
      onTouchStart={onD}
      onTouchMove={onM}
      onTouchEnd={onU}
      style={{
        width: "100%",
        maxWidth: 550,
        aspectRatio: "1",
        cursor: dragging ? "grabbing" : "grab",
        userSelect: "none",
        touchAction: "none",
        margin: "0 auto",
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${sz} ${sz}`}
        style={{ overflow: "visible" }}
      >
        <defs>
          <radialGradient id="skillGlobeGlow" cx="50%" cy="45%" r="50%">
            <stop offset="0%" stopColor="#DEB887" stopOpacity="0.06" />
            <stop offset="100%" stopColor="transparent" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="skillGlobeInner" cx="45%" cy="40%" r="50%">
            <stop offset="0%" stopColor="#1a1408" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#050301" stopOpacity="0.95" />
          </radialGradient>
          <radialGradient id="skillPhotoBlend" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="white" stopOpacity="0.85" />
            <stop offset="55%" stopColor="white" stopOpacity="0.4" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <mask id="skillPhotoMask">
            <circle cx={CX} cy={CY} r={R * 0.82} fill="url(#skillPhotoBlend)" />
          </mask>

          {/* === GLOW FILTERS === */}
          {/* small glow for dots and light particles */}
          <filter id="glowSmall" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feFlood floodColor="#DEB887" floodOpacity="0.6" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* large soft glow for icon backdrop */}
          <filter id="glowLarge" x="-150%" y="-150%" width="400%" height="400%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feFlood floodColor="#DEB887" floodOpacity="0.3" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* intense glow for hovered icons */}
          <filter id="glowHover" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="12" result="blur" />
            <feFlood floodColor="#DEB887" floodOpacity="0.5" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="glow" />
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <circle cx={CX} cy={CY} r={R * 2.2} fill="url(#skillGlobeGlow)" />
        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="url(#skillGlobeInner)"
          stroke="#DEB887"
          strokeWidth="0.6"
          opacity="0.45"
        />

        <g mask="url(#skillPhotoMask)">
          <image
            href={imageahmada}
            x={CX - R * 0.82}
            y={CY - R * 0.82}
            width={R * 1.64}
            height={R * 1.64}
            preserveAspectRatio="xMidYMid slice"
            opacity="0.55"
            style={{ mixBlendMode: "screen" }}
          />
        </g>

        {dotsEl}
        {gridEl}
        {ringEl}
        {arcEl}
        {partEl}

        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="none"
          stroke="#DEB887"
          strokeWidth="1.2"
          opacity="0.2"
        >
          <animate
            attributeName="opacity"
            values="0.15;0.3;0.15"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>

        {/* skill nodes — z-sorted */}
        {skillNodes.map((n) => n.el)}
      </svg>
    </div>
  );
}
